// ThemeToggle — palette button + 4-option dropdown, parallel to NotificationBell.
//
// Reuses useTheme().setTheme as the SOLE write path (same as the Settings
// AppearanceSettings picker): localStorage + profiles.appearance + live
// theme application all flow through that single call, so the header and
// Settings can never diverge.
//
// Two variants matching the NotificationBell pattern:
//   sidebar  + collapsed=false → palette icon + "Theme" label + current chip
//   sidebar  + collapsed=true  → icon-only
//   topbar                     → icon-only (mobile sticky top bar)
//
// Gated on SHOW_APPEARANCE_TAB so the header bypass can't outlive the
// Settings tab — if Appearance is hidden, no theme UI exists at all.
//
// Click-outside dismissal is implemented (unlike the JobDetail hold menu)
// because this menu lives in persistent chrome — without click-outside the
// menu would feel stuck open while the operator navigates the rest of the
// app. Listener registers only while open.

import { useEffect, useRef, useState } from 'react'
import { Palette, Sun, Moon, BookOpen, Monitor, Check } from 'lucide-react'
import { useTheme, type Theme } from '@/lib/theme'
import { SHOW_APPEARANCE_TAB } from '@/lib/featureFlags'

type Variant = 'sidebar' | 'topbar'

const OPTIONS: { value: Theme; label: string; icon: React.ComponentType<any> }[] = [
  { value: 'light',  label: 'Light',  icon: Sun },
  { value: 'dark',   label: 'Dark',   icon: Moon },
  { value: 'sepia',  label: 'Sepia',  icon: BookOpen },
  { value: 'system', label: 'System', icon: Monitor },
]

export default function ThemeToggle({
  variant, collapsed = false,
}: { variant: Variant; collapsed?: boolean }) {
  if (!SHOW_APPEARANCE_TAB) return null

  const { theme, resolvedTheme, setTheme } = useTheme()
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const handlePick = (next: Theme) => {
    setTheme(next)
    setOpen(false)
  }

  const triggerLabel = (() => {
    if (theme === 'system') return `System (${resolvedTheme})`
    return OPTIONS.find(o => o.value === theme)?.label ?? 'Theme'
  })()

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      {variant === 'sidebar' ? (
        <SidebarTrigger collapsed={collapsed} active={open} label={triggerLabel} onClick={() => setOpen(o => !o)} />
      ) : (
        <TopbarTrigger active={open} onClick={() => setOpen(o => !o)} />
      )}
      {open && (
        <div
          role="menu"
          aria-label="Theme"
          style={{
            position: 'absolute',
            // Sidebar: open BELOW the trigger, aligned to the sidebar's left
            // edge so the menu stays inside the viewport when collapsed. Topbar
            // (mobile): open below the trigger, aligned to the right so it
            // doesn't fly off-screen on narrow phones.
            top: 'calc(100% + 6px)',
            ...(variant === 'sidebar' ? { left: 0 } : { right: 0 }),
            minWidth: 180,
            background: 'var(--surface-raised)',
            border: '1px solid var(--border-default)',
            borderRadius: 10,
            padding: 6,
            boxShadow: 'var(--shadow-popover-mid)',
            zIndex: 60,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          {OPTIONS.map(opt => {
            const Icon = opt.icon
            const isActive = theme === opt.value
            return (
              <button
                key={opt.value}
                type="button"
                role="menuitemradio"
                aria-checked={isActive}
                onClick={() => handlePick(opt.value)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '7px 10px',
                  background: isActive ? 'var(--accent-dim)' : 'transparent',
                  border: `1px solid ${isActive ? 'var(--accent-border)' : 'transparent'}`,
                  borderRadius: 7,
                  cursor: 'pointer',
                  color: isActive ? 'var(--accent-text)' : 'var(--ink-primary)',
                  fontSize: 13, fontWeight: 500,
                  textAlign: 'left',
                }}
                onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = 'var(--hover-bg)' }}
                onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent' }}
              >
                <Icon style={{ width: 14, height: 14, flexShrink: 0 }} />
                <span style={{ flex: 1 }}>{opt.label}</span>
                {isActive && <Check style={{ width: 13, height: 13, color: 'var(--accent-text)' }} />}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

function SidebarTrigger({ collapsed, active, label, onClick }: {
  collapsed: boolean; active: boolean; label: string; onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={collapsed ? 'Theme' : undefined}
      aria-label="Theme"
      aria-haspopup="menu"
      aria-expanded={active}
      style={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        justifyContent: collapsed ? 'center' : 'flex-start',
        padding: collapsed ? '8px' : '7px 10px',
        background: active ? 'var(--sidebar-faint-bg)' : 'none',
        border: 'none',
        borderRadius: 8,
        cursor: 'pointer',
        color: 'var(--sidebar-meta-ink)',
        textAlign: 'left',
        transition: 'background 150ms ease, color 150ms ease',
      }}
      onMouseEnter={e => { e.currentTarget.style.background = 'var(--sidebar-faint-bg)' }}
      onMouseLeave={e => { e.currentTarget.style.background = active ? 'var(--sidebar-faint-bg)' : 'none' }}
    >
      <Palette style={{ width: 15, height: 15, flexShrink: 0 }} />
      {!collapsed && (
        <span style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', overflow: 'hidden', gap: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            Theme
          </span>
          <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--sidebar-meta-ink)', textTransform: 'capitalize', flexShrink: 0 }}>
            {label}
          </span>
        </span>
      )}
    </button>
  )
}

function TopbarTrigger({ active, onClick }: { active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Theme"
      aria-haspopup="menu"
      aria-expanded={active}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: active ? 'var(--hover-bg)' : 'none',
        border: 'none',
        borderRadius: 8,
        cursor: 'pointer',
        color: 'var(--ink-primary)',
        padding: 8,
        flexShrink: 0,
      }}
    >
      <Palette style={{ width: 20, height: 20 }} />
    </button>
  )
}
